/**
 * Help Center: "account" section articles. Format and rules: src/content/help/README.md.
 */
export default [
  {
    slug: 'create-account',
    title: 'Creating your account',
    summary: 'Your Defera account holds your trips, payments, Defera Credit and Miles. You sign in with your email address, so choose one you’ll keep and check often.',
    keywords: ['sign up', 'register', 'new account', 'join', 'open an account', 'get started'],
    related: ['account/sign-in-and-password', 'account/update-profile', 'account/saved-passengers', 'getting-started/how-defera-works'],
    updated: '2026-10-23',
    body: `Your Defera account is where your trips, payments, Defera Credit and Defera Miles live. You sign in to it with your email address, so choose one you'll keep and check often.

That email address is your sign-in identity, and there's no self-serve way to change it later. So it's worth a moment's thought before you start. If anything gets in the way while you're setting up, contact us.

## Which email address should I use?

Pick an address you plan to keep for as long as you travel with us. Here's why it matters:

- **It's how you sign in.** Your email address is shown on your Profile, but it can't be edited there. Changing it later means sending a request to our team.
- **It's where trip documents go.** We can only send trip documents to the email address on your account.
- **It's where reset links go.** If you ever forget your password, the secure reset link is emailed to that address.

So choose an address you check often.

## What will I find in my account?

Once you're signed in, the main menu has four places to go, and your Profile is in your avatar menu:

- **Home:** destinations and highlights from your upcoming trips.
- **Book:** flight search, plus **Stays** for Flight + Hotel packages.
- **Trips:** every booking, with its payment schedule, **Flight Options** and Trip Statement.
- **Rewards:** your Defera Miles balance, your Defera Status progress and e-gift cards.
- **Profile:** Profile Details and Passengers, plus a Defera Credits card if you have Defera Credit.

Defera Credit is credit on your Defera account that you can use toward a future booking. Defera Miles are rewards you earn on completed trips.

## How do I set up my profile?

Setting it up now saves typing when you book:

1. **Open your Profile.** Open your avatar menu and choose **Profile**.
2. **Check your name.** In Profile Details, check that your first and last name are right.
3. **Save the people you book for.** In the Passengers section, tap **Add** to save a traveler. Future bookings fill in their details from a dropdown.

[Saving travelers to your profile](/help/account/saved-passengers) has more on Passengers.

## Can I use Defera on my phone?

Yes. Defera works in your phone's web browser, and you can add it to your home screen so it opens like an app. [Using Defera on your phone](/help/account/use-defera-on-your-phone) shows how.

## Having trouble?

If you run into a problem creating your account, send us a message from the [Contact page](/contact#message) or call [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}). The Defera Concierge, our AI concierge, answers the phone 24/7 and can send a request to our team. Our team replies within 24 hours.`,
  },
  {
    slug: 'sign-in-and-password',
    title: 'Signing in and resetting your password',
    summary: 'Forgot your password? Tap “Forgot password?” on the sign-in page and enter your account’s email address. We’ll email you a secure link to reset it.',
    keywords: ['forgot password', 'reset password', 'log in', 'login', 'can’t sign in', 'locked out', 'password reset email'],
    related: ['account/account-security', 'account/update-profile', 'emails/missing-email', 'contact/how-to-contact'],
    updated: '2026-10-23',
    body: `Forgot your password? Tap **Forgot password?** on the sign-in page and enter the email address on your Defera account. We'll email you a secure link to reset it.

Being signed out doesn't affect your trips. Your payments are still charged automatically on their dates, so you can sort out your sign-in without worrying about your plan.

## How do I reset my password?

Your account's email address is your sign-in identity, so that's where the reset link goes. If you sign in with a password, here's what to do:

1. **Start on the sign-in page.** Tap **Forgot password?**
2. **Enter your email address.** Use the email address on your Defera account, the one you sign in with.
3. **Open the email we send you.** It has a secure link. Use it to reset your password, then sign in with your new one.

## What if the reset email doesn't arrive?

- **Check the address you entered.** The reset link goes to the address you type, so make sure it's the one on your Defera account. If you spot a typo, tap **Forgot password?** again and re-enter it.
- **Check your spam or junk folder.** Emails sometimes land there.

[I didn't get an email from Defera](/help/emails/missing-email) has more things to check. Still can't get in? Send us a message from the [Contact page](/contact#message) or call [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}).

## Can I change the email I sign in with?

Your sign-in email is shown on your Profile, but you can't edit it there, because it's your sign-in identity. To change it, send a request to our team. [Updating your profile](/help/account/update-profile) explains how.

## What should I have ready when I contact you?

The Defera Concierge, our AI concierge, answers the phone 24/7 and can send a request to our team. Before it shares any account details on a call, it asks for two things, so have them handy:

- **Your booking reference, or the email on your account.** Your booking reference is the code at the top of your booking confirmation email.
- **The passenger's name.**

If you'd rather write, send us a message from the [Contact page](/contact#message). Our team replies within 24 hours.`,
  },
  {
    slug: 'update-profile',
    title: 'Updating your profile',
    summary: 'Change your first and last name in Profile Details. Your email is read-only because you sign in with it. Profile also holds Passengers and any Defera Credit.',
    keywords: ['edit profile', 'change my name', 'change email', 'account settings', 'personal details', 'profile page', 'available credit'],
    related: ['account/saved-passengers', 'account/sign-in-and-password', 'payments/update-card', 'changes-cancellations/correct-traveler-details'],
    updated: '2026-10-23',
    body: `You can update your first and last name in Profile Details on your Profile. Your email address is shown there too, but it's read-only, because it's the address you sign in with.

Your Profile also holds Passengers, your list of saved travelers, and, if you have Defera Credit, a Defera Credits card. Here's what you can change there, and where to go for the things that live somewhere else.

## How do I open my Profile?

Profile isn't in the main menu with **Home**, **Book**, **Trips** and **Rewards**. Open your avatar menu and choose **Profile**.

## What's on my Profile?

Your Profile has up to three parts:

- **Profile Details.** Your first and last name, which you can edit, and your email address, which you can see but not change.
- **Passengers.** The travelers you've saved for future bookings. Tap **Add** to save someone new, or use the edit and delete icons on a traveler's row.
- **Defera Credits.** This card appears only if you have Defera Credit, either a balance or any history. It shows your "Available credit" and its history, with entries such as "Credit added", "Credit applied", "Held for checkout" and "Returned to balance".

Defera Credit is credit on your Defera account that you can use toward a future Defera booking. If your credit doesn't look right, see [Where's my Defera Credit?](/help/changes-cancellations/where-is-my-credit)

## What isn't on my Profile?

A couple of things live somewhere else:

- **Your card.** There's no Payment Methods page. To change the card your payments are charged to, open **Trips**, tap the trip, then tap **Update Card** in the trip's payment schedule.
- **Date of birth and phone number.** Profile Details has no date of birth or phone field. Each traveler's date of birth and contact details are saved with that traveler in Passengers.

## Can I change my sign-in email?

There's no self-serve way to change the email you sign in with. If you need it changed, send a request to our team: [chat with the Defera Concierge](/support), our AI concierge, and it can send the request for you. You can also send us a message from the [Contact page](/contact#message). Our team reviews it and replies by email.

### Need trip details sent somewhere else?

You don't need to change your account email to share a trip. Every trip has a downloadable Trip Statement, a PDF with your itinerary, travelers, pricing and payments. Open the trip in **Trips** and tap the download icon in the trip's header row.

You can forward the PDF to anyone yourself, such as an employer or a travel companion, or include it with a visa application. We can only send trip documents to the email address on your account.

## How do I fix a name on a trip I've already booked?

Saved travelers fill in future bookings. To correct a traveler's name or date of birth on a trip you've already booked, see [Fixing a name or date of birth on your booking](/help/changes-cancellations/correct-traveler-details).`,
  },
  {
    slug: 'saved-passengers',
    title: 'Saving travelers to your profile',
    summary: 'Save the people you travel with in Passengers on your Profile. When you book, pick them from a dropdown and their details fill in for you.',
    keywords: ['saved passengers', 'travelers', 'family members', 'frequent flyer number', 'known traveler number', 'passport', 'autofill', 'TSA PreCheck'],
    related: ['booking/traveler-information', 'booking/booking-for-others', 'seats-and-bags/frequent-flyer-and-precheck', 'account/update-profile'],
    updated: '2026-10-05',
    body: `Save the people you travel with in Passengers on your Profile. When you book, you can choose them from a dropdown, and their details fill in for you.

It saves typing the same details every time, and it's especially handy for family and group bookings. You add a traveler with the **Add** button, and you can edit or delete each one from their row in the list.

## How do I add a traveler?

1. **Open your Profile.** Open your avatar menu and choose **Profile**.
2. **Start a new traveler.** In the Passengers section, tap **Add** at the top right.
3. **Enter their details and save.** Enter everything exactly as it appears on the traveler's government ID.

## What can I save?

You can save these details for each traveler:

- Full legal name, as it appears on their government ID
- Date of birth and gender
- Contact information
- Passport details, for international trips
- Frequent flyer numbers. All major airline loyalty programs are supported, such as MileagePlus, AAdvantage, SkyMiles, Executive Club, Skywards and Miles & More.
- TSA PreCheck, Global Entry or Known Traveler Number

A Known Traveler Number is the number that comes with programs such as TSA PreCheck and Global Entry.

## Why does the name have to match their ID?

The name on a booking needs to match the government ID the traveler shows when they fly. Saving their full legal name exactly as it appears on that ID means it's filled in the same way every time you book for them.

## How do I edit or delete a traveler?

Each traveler in Passengers has an edit icon and a delete icon on their row:

- **To update someone's details,** such as a new passport, tap the edit icon.
- **To remove a traveler** you no longer book for, tap the delete icon.

## How do I use saved travelers when I book?

When you book, choose a saved traveler from the dropdown and their details fill in, so you don't have to type them again. Look them over before you continue, especially if someone has renewed their passport.

## What about trips I've already booked?

Saved travelers fill in future bookings. To correct a name or date of birth on a trip you've already booked, see [Fixing a name or date of birth on your booking](/help/changes-cancellations/correct-traveler-details).`,
  },
  {
    slug: 'account-security',
    title: 'Keeping your account secure',
    summary: 'We encrypt your connection and never store your complete card details. Look after the email you sign in with, and contact us if anything looks wrong.',
    keywords: ['hacked', 'suspicious activity', 'protect my account', 'secure', 'someone used my account', 'safety'],
    related: ['account/payment-security', 'account/sign-in-and-password', 'account/privacy-and-data', 'emails/is-this-email-from-defera'],
    updated: '2026-10-23',
    body: `We protect your Defera account with an encrypted connection and secure payment handling, and we never store your complete card details. You can help keep it safe too, mostly by looking after the email account you sign in with.

If anything on your Defera account ever looks wrong, reset your password and contact us right away, so our team can take a look.

## What does Defera do to protect my account?

- **Your connection is encrypted.** Your connection to Defera is TLS-encrypted, which means what you send is scrambled on its way to us.
- **We never store your complete card details.** Payments are processed by certified PCI-DSS payment gateways using tokenization, where a secure token stands in for your card number.
- **Saved cards are replaced, not edited.** A saved card can't be edited in place. **Update Card** takes the full card details in one secure flow and replaces the old card.
- **Emails show only part of your ID numbers.** Your booking confirmation email shows only the last 4 digits of passport, ID and Known Traveler numbers. The full numbers are on your trip, in Flight Details.
- **Trip documents go to your account email.** We only send trip documents to the email address on your account.
- **We check who's calling.** Before it shares any account details on a call, the Defera Concierge asks for your booking reference (or the email on your account) and the passenger's name.

[Account protection](/security/account-protection), in our Security Center, explains each of these in more detail.

## What can I do to keep my account safe?

- **Look after your sign-in email.** Your account's email address is your sign-in identity, and password reset links are sent to it. Keep that email account secure, with a strong password of its own.
- **Reset your password when you need to.** Tap **Forgot password?** on the sign-in page and we'll email you a secure reset link. [Signing in and resetting your password](/help/account/sign-in-and-password) walks through it.
- **Change your card only through Update Card.** It's the only place in Defera to change the card on file: open **Trips**, tap the trip, then tap **Update Card** in the trip's payment schedule.
- **Share trips with the Trip Statement.** To share a trip with a travel companion or an employer, download its Trip Statement from the trip's header row and forward the PDF. There's no need to share your sign-in.

## How can I tell an email is really from Defera?

Your booking confirmation email comes from bookings@mail.defera.app, the moment you book. If an email asks for something unexpected, or doesn't look right, check it before you click anything. [How to tell an email is really from Defera](/help/emails/is-this-email-from-defera) shows what to look for.

## What if something looks wrong?

If you see a booking, payment or change on your account that you don't recognize, here's what to do:

1. **Reset your password.** Tap **Forgot password?** on the sign-in page and follow the secure link we email you.
2. **Contact us right away.** [Chat with the Defera Concierge](/support), our AI concierge, or call [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}), where the same AI concierge answers by voice 24/7. It can send a request to our team to take a look.

You can also send us a message from the [Contact page](/contact#message). Our team replies within 24 hours.`,
  },
  {
    slug: 'payment-security',
    title: 'How we protect your payment details',
    summary: 'We never store your complete card details. Your card travels over an encrypted connection, and payments are handled by certified PCI-DSS payment gateways.',
    keywords: ['card security', 'is my card safe', 'PCI', 'encryption', 'tokenization', 'saved card', 'card details'],
    related: ['payments/update-card', 'payments/accepted-payment-methods', 'account/account-security', 'getting-started/is-defera-safe'],
    updated: '2026-10-23',
    body: `We never store your complete card details. Your card information goes over an encrypted connection straight to our payment processor, and payments are processed by certified payment gateways that keep your card number protected.

It's natural to think twice before saving a card. Here's what happens to your card details, in plain words, and how to update them.

## How is my card protected?

Three things work together:

- **An encrypted connection, straight to the processor.** You type your card details into the payment processor's secure card fields, and they travel over a TLS-encrypted connection straight to the processor. They're scrambled on the way and never pass through our servers.
- **Certified payment gateways.** Payments are processed by certified PCI-DSS payment gateways. PCI-DSS is the card industry's security standard.
- **Tokenization.** The gateways use tokenization, which means a secure token stands in for your card number. We never store your complete card details.

[Payment security](/security/payments), in our Security Center, follows your card from checkout to your last payment.

## Why does checkout save my card?

Every payment plan needs a card on file, because your payments are charged automatically on their schedule. Your card on file is the debit or credit card saved for your payments.

That's why checkout asks for your card even when Defera Credit, the credit on your Defera account, covers everything due today. Your card is saved securely for your future payments, and it's charged $0 at booking when credit covers what's due. The "Due today" line on your checkout page shows what your card will be charged.

For example, if your Defera Credit covers your whole deposit, "Due today" shows $0. Your card isn't charged at booking, and it's saved for the payments that follow.

## How do I update my card?

For security, a saved card can't be edited in place, so you can't change its CVC or expiration date on its own. Instead, **Update Card** takes your full card details in one secure flow and replaces the old card:

1. **Open your trip.** Open **Trips** and tap your trip.
2. **Tap Update Card.** It's in the trip's payment schedule, next to **Make a Payment**.
3. **Enter your card details and save.** Enter the card number, expiration date, CVC and ZIP code. Your future payments use the new card automatically.

If an update doesn't go through, nothing changes on your plan: the card already on file keeps being charged on schedule.

There's no Payment Methods page on your Profile. **Update Card**, inside your trip, is the only place to change your card. [Updating your card](/help/payments/update-card) has more.

## Which cards can I use?

We accept debit and credit cards. Prepaid cards, including gift and stored-value cards, aren't accepted on payment plans. [What payment methods can I use?](/help/payments/accepted-payment-methods) has the full list.

## See a charge you don't recognize?

Start by checking it against your trip. [A charge I don't recognize](/help/payments/unrecognized-charge) shows how. If something still looks wrong, [chat with the Defera Concierge](/support), our AI concierge, or send us a message from the [Contact page](/contact#message). Our team replies within 24 hours.`,
  },
  {
    slug: 'privacy-and-data',
    title: 'Your privacy and your data',
    summary: 'We ask for the details a booking needs, encrypt your connection and never store your complete card details. You can delete your account from your Profile.',
    keywords: ['personal information', 'GDPR', 'CCPA', 'data protection', 'privacy policy', 'personal data', 'what data you collect'],
    related: ['account/delete-account', 'account/account-security', 'booking/traveler-information', 'emails/email-preferences'],
    updated: '2026-10-23',
    body: `To book your trip, we ask for the traveler details a flight booking needs and a card for your payments. We protect them: your connection is encrypted, and we never store your complete card details.

You stay in control of what's saved. You can edit or delete your saved travelers yourself, and you can delete your account in **Profile → Delete account**. Our Privacy Policy explains in full how we handle your personal data.

## What do you ask for, and why?

Here's what a booking asks for, and why each part is there:

- **Each traveler's full legal name, date of birth and gender.** We need these to book. The name must match the traveler's government ID.
- **Contact information.** We need contact details for at least one traveler to book.
- **Passport number, expiration date and nationality.** We need these for international flights.
- **Frequent flyer numbers, Known Traveler Number and special requests.** These are optional, if you'd like them on your booking.
- **A debit or credit card.** Your payments are charged automatically to the card on file.

## How do you protect it?

- **An encrypted connection.** Your connection to Defera is TLS-encrypted, so what you send is scrambled on its way to us.
- **No complete card details stored.** Payments are processed by certified PCI-DSS payment gateways using tokenization, where a secure token stands in for your card number.
- **Only part of your ID numbers in emails.** Your booking confirmation email shows only the last 4 digits of passport, ID and Known Traveler numbers. The full numbers are on your trip, in Flight Details.
- **Trip documents go to your account email.** We only send trip documents to the email address on your account.
- **Checks on calls.** Before it shares any account details on a call, the Defera Concierge asks for your booking reference (or the email on your account) and the passenger's name.

[Data protection](/security/data-protection), in our Security Center, explains how your data is encrypted, stored and backed up.

## What can I change myself?

- **Saved travelers:** edit or delete anyone in the Passengers section of your Profile, from their row.
- **Emails:** [Unsubscribing from Defera emails](/help/emails/email-preferences) explains what you can turn off, and how to ask about anything else.
- **Advertising and analytics:** opt out of the sale or sharing of your personal information on [Your privacy choices](/privacy-choices). We also treat a Global Privacy Control signal from your browser as an opt-out.

## Can I delete my account?

Yes. Account deletion is available by contacting us, and our team takes care of it. [Closing your account](/help/account/delete-account) explains how to ask.

## Where can I read the full policy?

Our [Privacy Policy](/privacy) explains how we handle your personal data. If you have a question it doesn't answer, send us a message from the [Contact page](/contact#message). Our team replies within 24 hours.`,
  },
  {
    slug: 'delete-account',
    title: 'Deleting your account',
    summary: 'Delete your Defera account in Profile → Delete account, on the website or in the app. If a trip or credit is still open, we settle it with you first.',
    keywords: ['delete account', 'deactivate', 'remove my account', 'cancel account', 'close account', 'account deletion', 'erase my data'],
    related: ['account/privacy-and-data', 'contact/how-to-contact', 'emails/email-preferences', 'changes-cancellations/cancel-your-trip'],
    updated: '2026-10-08',
    body: `You can delete your Defera account yourself, on our website or in the Defera app.

## How do I delete my account?

1. **Open your Profile.** Open your avatar menu and choose **Profile**.
2. **Choose Delete account.** It's the last section on the page.
3. **Review and confirm.** We show you what will be deleted and what we keep. Type **DELETE** to confirm.

If nothing is still open on your account, it's deleted right away and you're signed out.

## What if I still have a trip or credit?

We need to settle a few things with you first:

- **A trip that's still active,** including one you're still paying for.
- **A Reserve plan.**
- **Defera Credit.**
- **A balance with our collections team.**

You can still ask to delete your account from your Profile. Our team contacts you by email within 2 business days. For a trip, you can cancel it or keep it until you've traveled. We delete your account as soon as everything is settled.

## What's deleted, and what do you keep?

- **Deleted:** your profile, saved travelers, saved cards, linked cards, chats and preferences. Your login is removed, so you can't sign in to the account again.
- **Kept:** records of your trips and payments, which the law requires us to keep, for example for accounting and tax. We remove the personal details they don't need, such as passport numbers and dates of birth.
- **Lost:** any Defera Miles on the account.

Deleting your account can't be undone.

## Want to keep your trip records?

Before you delete your account, download each trip's Trip Statement. It's a PDF with your itinerary, travelers, pricing and payment history. Open the trip in **Trips** and tap the download icon in the trip's header row.

## Want to cancel a trip instead?

Deleting your account and cancelling a trip are separate. To cancel a trip, open it in **Trips**, tap **Flight Options**, then **Cancel**. Your cancellation screen shows your options before you confirm anything. [How to cancel your trip](/help/changes-cancellations/cancel-your-trip) walks through it.

## Want fewer emails instead?

You don't need to delete your account to get fewer emails. [Unsubscribing from Defera emails](/help/emails/email-preferences) explains what you can turn off.

## Where can I read about my personal data?

Our [Privacy Policy](/privacy) explains how we handle your personal data.`,
  },
  {
    slug: 'use-defera-on-your-phone',
    title: 'Using Defera on your phone',
    summary: 'Defera works fully in your phone’s browser, and you can add it to your home screen to open it like an app: on iPhone, tap Share, then Add to Home Screen.',
    keywords: ['mobile', 'app', 'iPhone', 'Android', 'home screen', 'install', 'PWA', 'download the app'],
    related: ['account/sign-in-and-password', 'payments/view-payment-schedule', 'contact/how-to-contact', 'getting-started/how-defera-works'],
    updated: '2026-10-05',
    body: `Defera works fully in your phone's web browser, so you can search, book and manage your trips from your phone. You can also add Defera to your home screen, so it opens like any other app.

You don't need to download anything from an app store. Adding Defera to your home screen gives it its own icon, and it's the same Defera you use in your browser, with the same account and the same trips.

## How do I add Defera to my home screen?

It depends on your phone and your browser.

### On iPhone or iPad

1. **Open Defera.** Open Defera in your browser.
2. **Open the Share menu.** Tap **Share**.
3. **Add it.** Choose **Add to Home Screen**, then confirm.

Defera's icon appears on your home screen. Tap it to open Defera like an app.

### In Chrome or Edge

Chrome and Edge offer to install Defera themselves. When your browser shows the install prompt, accept it, and Defera is added like an app.

## Where do I find things on a small screen?

The layout adapts to your phone, so a few things sit in different places:

- The main menu has **Home**, **Book**, **Trips** and **Rewards**. Your Profile is in your avatar menu.
- On a phone, your trip's payment schedule sits at the top of the trip page, with buttons such as **Make a Payment** and **Update Card**.
- **Flight Options**, the menu for changing your flight, buying seats or bags, and cancelling, is at the top right of the trip card.
- Your trip's header row has buttons to message or call us, and a download icon for your Trip Statement.

## Can I add my flights to my phone's calendar?

Yes. Your booking confirmation email has an **Add to calendar** button. It adds every flight to your calendar at the right local times, and it works with Apple Calendar, Outlook and most calendar apps.

## Can I get help from my phone?

Yes. [Chat with the Defera Concierge](/support), our AI concierge, from your phone, or call [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}), where the same AI concierge answers by voice 24/7. You can also reach us from any trip, with the message and call buttons in the trip's header row.`,
  },
];
